import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  LayoutDashboard, MessageSquare, Heart, Settings, Sparkles,
  Users, Megaphone, Newspaper, CalendarDays, Building2, BookOpen,
  Sun, Moon, Rows3, Rows4,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useDensity } from '../hooks/useDensity';
import { useAuth } from '../context/AuthContext';

/**
 * Global Cmd+K command palette. Opens with `⌘K` / `Ctrl+K` from anywhere
 * in the app. Provides quick navigation, theme + density toggles, and
 * (future) admin-only shortcuts.
 *
 * Pure client-side — no API calls. Lives at App level.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { themePreference, setThemePreference } = useTheme();
  const { isCompact, toggle: toggleDensity } = useDensity();
  const { user } = useAuth();

  // Bind ⌘K / Ctrl+K — also Esc closes (CommandDialog handles that).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on route change so the palette doesn't linger over the new page.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const go = useCallback((to) => {
    setOpen(false);
    navigate(to);
  }, [navigate]);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, to: '/dashboard' },
    { id: 'messages', label: 'Messages', icon: MessageSquare, to: '/messages' },
    { id: 'saved', label: 'Saved items', icon: Heart, to: '/saved' },
    { id: 'settings', label: 'Account settings', icon: Settings, to: '/account/settings' },
    { id: 'casting', label: 'Browse casting calls', icon: Sparkles, to: '/casting-calls' },
    { id: 'talent', label: 'Browse talent', icon: Users, to: '/talent' },
    { id: 'agents', label: 'Browse agents', icon: Building2, to: '/agents' },
    { id: 'services', label: 'Industry services', icon: Building2, to: '/industry-services' },
    { id: 'events', label: 'Events', icon: CalendarDays, to: '/events' },
    { id: 'news', label: 'News', icon: Newspaper, to: '/news' },
    { id: 'support', label: 'Support', icon: BookOpen, to: '/support' },
  ];

  const adminItems = user?.active_role === 'admin'
    ? [
        { id: 'admin-approvals', label: 'Admin · Approvals', to: '/dashboard?tab=approvals' },
        { id: 'admin-users', label: 'Admin · Users', to: '/dashboard?tab=users' },
        { id: 'admin-content', label: 'Admin · Content & Branding', to: '/dashboard?tab=content' },
        { id: 'admin-broadcast', label: 'Admin · Broadcast', to: '/dashboard?tab=content', icon: Megaphone },
      ]
    : [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, settings, actions…" data-testid="cmdk-input" />
      <CommandList>
        <CommandEmpty>No matches. Try “messages”, “settings”, “talent”…</CommandEmpty>

        <CommandGroup heading="Go to">
          {navItems.map((item) => {
            const Icon = item.icon || LayoutDashboard;
            return (
              <CommandItem
                key={item.id}
                onSelect={() => go(item.to)}
                value={`${item.label} ${item.id}`}
                data-testid={`cmdk-${item.id}`}
              >
                <Icon className="mr-2 h-4 w-4 text-theme-muted" />
                <span>{item.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        {adminItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Admin">
              {adminItems.map((item) => (
                <CommandItem
                  key={item.id}
                  onSelect={() => go(item.to)}
                  value={item.label}
                  data-testid={`cmdk-${item.id}`}
                >
                  <span className="ml-6">{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Preferences">
          <CommandItem
            onSelect={() => {
              const next = themePreference === 'dark' ? 'light' : 'dark';
              setThemePreference(next);
              setOpen(false);
            }}
            value="theme dark light toggle"
            data-testid="cmdk-toggle-theme"
          >
            {themePreference === 'dark' ? (
              <Sun className="mr-2 h-4 w-4 text-theme-muted" />
            ) : (
              <Moon className="mr-2 h-4 w-4 text-theme-muted" />
            )}
            Switch to {themePreference === 'dark' ? 'light' : 'dark'} mode
          </CommandItem>
          <CommandItem
            onSelect={() => { toggleDensity(); setOpen(false); }}
            value="density compact comfortable rows"
            data-testid="cmdk-toggle-density"
          >
            {isCompact ? (
              <Rows4 className="mr-2 h-4 w-4 text-theme-muted" />
            ) : (
              <Rows3 className="mr-2 h-4 w-4 text-theme-muted" />
            )}
            Switch to {isCompact ? 'comfortable' : 'compact'} rows
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
